import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { omit, isEqual } from 'lodash';
import { blurActiveAndGetFinalValues, scrollToFirstError } from './use-form-helpers';

//Introduced to clear out issubmitting after validation done.

function omitUndefined(obj) {
  const newObj = _objectSpread({}, obj);
  Object.keys(newObj).forEach(key => newObj[key] === undefined && delete newObj[key]);
  return newObj;
}
function initialise(initialState) {
  return initialState;
}
const useFormReducer = (initialState, validationFn) => {
  const {
    0: state,
    1: dispatch
  } = useReducer((state, action) => {
    switch (action.type) {
      case 'CHANGE_VALUE':
        {
          const {
            name,
            value
          } = action.payload;
          const newValues = _objectSpread(_objectSpread({}, state.values), {}, {
            [name]: value
          });
          return _objectSpread(_objectSpread({}, state), {}, {
            values: omitUndefined(newValues)
          });
        }
      case 'BLUR_VALUE':
        {
          const {
            name,
            value
          } = action.payload;
          const newValues = _objectSpread(_objectSpread({}, state.values), {}, {
            [name]: value
          });
          const newErrors = validationFn(newValues, state.isSubmitting);
          return _objectSpread(_objectSpread({}, state), {}, {
            values: omitUndefined(newValues),
            errors: newErrors
          });
        }
      case 'CHANGE_VALUES':
        {
          const newValues = action.payload.reduce((acc, _ref) => {
            let {
              name,
              value
            } = _ref;
            return _objectSpread(_objectSpread({}, acc), {}, {
              [name]: value
            });
          }, state.values);
          return _objectSpread(_objectSpread({}, state), {}, {
            values: omitUndefined(newValues)
          });
        }
      case 'BLUR_VALUES':
        {
          const newValues = action.payload.reduce((acc, _ref2) => {
            let {
              name,
              value
            } = _ref2;
            return _objectSpread(_objectSpread({}, acc), {}, {
              [name]: value
            });
          }, state.values);
          const newErrors = validationFn(newValues, state.isSubmitting);
          return _objectSpread(_objectSpread({}, state), {}, {
            values: newValues,
            errors: newErrors
          });
        }
      case 'PRE_SUBMIT':
        {
          const newErrors = validationFn(state.values, true);
          return _objectSpread(_objectSpread({}, state), {}, {
            isSubmitting: true,
            errors: newErrors
          });
        }
      case 'POST_SUBMIT':
        {
          return _objectSpread(_objectSpread({}, state), {}, {
            isSubmitting: false
          });
        }
      case 'RESET_ERRORS':
        {
          const newErrors = action.payload ? omit(state.errors, action.payload) : {};
          return _objectSpread(_objectSpread({}, state), {}, {
            errors: _objectSpread({}, newErrors)
          });
        }
      case 'CLEAR_VALUES':
        {
          const newValues = action.payload ? omit(state.values, action.payload) : {};
          return _objectSpread(_objectSpread({}, state), {}, {
            isSubmitting: false,
            values: _objectSpread({}, newValues)
          });
        }
      case 'RESET_VALUES':
        {
          const newValues = _objectSpread({}, state.values);
          action.payload.forEach(field => newValues[field] = initialState.values[field]);
          return _objectSpread(_objectSpread({}, state), {}, {
            isSubmitting: false,
            values: omitUndefined(newValues)
          });
        }
      case 'INITIALISE':
        {
          return initialise(initialState);
        }
      default:
        {
          return _objectSpread({}, state);
        }
    }
  }, initialState, initialise);
  return [state, dispatch];
};
const trimValueIfString = e => typeof e.value === 'string' ? _objectSpread(_objectSpread({}, e), {}, {
  value: e.value.trim()
}) : e;

/**
 * Returns object of form values, corresponding object of form errors
 * and functions to handle onChange, onBlur and onSubmit form actions.
 * @param validate - a function called with the form values object
 * that returns a corresponding object of errors
 * @param callback - a function that will be called if the form validates
 * with no errors
 * @param initialValues - an optional object of with initial values to
 * instantiate the form with
 * @param CallbackFnArgs - a tuple of types corresponding to each argument
 * that will be passed into the callback function
 */
const useForm = function (validate, callback) {
  let initialValues = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let validateOnInit = arguments.length > 3 ? arguments[3] : undefined;
  let scrollToErrorMapping = arguments.length > 4 ? arguments[4] : undefined;
  const initialState = {
    values: initialValues,
    errors: validateOnInit ? validate(initialValues) : {},
    isSubmitting: false,
    lastInitialValues: initialValues
  };
  const [{
    values,
    errors,
    lastInitialValues
  }, dispatch] = useFormReducer(initialState, validate);

  // In case the initialValues haven't been loaded yet when useForm is called the first time
  useEffect(() => {
    if (!Object.keys(lastInitialValues).length && Object.keys(initialValues).length) {
      dispatch({
        type: 'INITIALISE'
      });
    }
  }, [dispatch, initialValues, lastInitialValues]);
  const {
    0: hasMounted,
    1: setHasMounted
  } = useState(false);
  useEffect(() => setHasMounted(true), []);
  const getFinalValuesAndApplyValidation = () => {
    dispatch({
      type: 'PRE_SUBMIT'
    });
    const valuesOnSubmit = blurActiveAndGetFinalValues(values, hasMounted ? undefined : () => undefined);
    const submitErrors = validate(valuesOnSubmit, true);
    const isErrors = Object.values(submitErrors).some(error => Boolean(error));
    if (isErrors && scrollToErrorMapping) {
      scrollToFirstError(submitErrors, scrollToErrorMapping, hasMounted ? undefined : () => undefined);
    }
    dispatch({
      type: 'POST_SUBMIT'
    });
    return {
      validatedValues: valuesOnSubmit,
      canSubmit: !isErrors
    };
  };

  /**
   * Accepts a callback which is called if there are no errors.
   * Sets errors and isSubmitting bool - we want errors to persist
   * in the case that validation prevents form submission.
   * @param args - An unknown number of arguments can be captured using
   * the rest parameter as a tuple/array these will later be provided to the callback
   */
  const handleSubmit = function (e) {
    e.preventDefault();
    const {
      canSubmit,
      validatedValues
    } = getFinalValuesAndApplyValidation();
    if (canSubmit) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      callback(validatedValues, ...args);
    }
  };
  const valuesAfterValidationRef = useRef(null);

  // N.B. These functions only work correctly if you can guarantee
  // no changes to the form between the calls to prepareForStaggeredSubmit and
  // completeStaggeredSubmit. Use with caution.
  const prepareForStaggeredSubmit = () => {
    const {
      canSubmit,
      validatedValues
    } = getFinalValuesAndApplyValidation();
    valuesAfterValidationRef.current = {
      canSubmit,
      validatedValues
    };
    return canSubmit;
  };
  const completeStaggeredSubmit = async function () {
    if (valuesAfterValidationRef.current === null) {
      throw new Error('completeStaggeredSubmit called before prepareForStaggeredSubmit');
    } else if (!valuesAfterValidationRef.current.canSubmit) {
      throw new Error('Tried to completeStaggeredSubmit() but prepareForStaggeredSubmit() was false');
    }
    const {
      validatedValues
    } = valuesAfterValidationRef.current;
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return await callback(validatedValues, ...args);
  };

  /**
   * Accepts 'onChange' events and store form values in form state
   */
  const handleChange = useCallback((e, trimString) => {
    dispatch({
      type: 'CHANGE_VALUE',
      payload: trimString ? trimValueIfString(e) : e
    });
  }, [dispatch]);
  const batchHandleChange = useCallback((events, trimString) => {
    const payload = events.map(e => trimString ? trimValueIfString(e) : e);
    dispatch({
      type: 'CHANGE_VALUES',
      payload
    });
  }, [dispatch]);

  /**
   * Accepts 'onBlur' events, validates the form and return errors for invalid input
   */
  const handleBlur = useCallback(e => {
    dispatch({
      type: 'BLUR_VALUE',
      payload: trimValueIfString(e)
    });
  }, [dispatch]);
  const batchHandleBlur = useCallback(events => {
    dispatch({
      type: 'BLUR_VALUES',
      payload: events.map(trimValueIfString)
    });
  }, [dispatch]);

  /**
   * Accepts an array of keys, removes specified keys from the values object
   */
  const clearValues = useCallback(arrayOfKeys => {
    dispatch({
      type: 'CLEAR_VALUES',
      payload: arrayOfKeys
    });
  }, [dispatch]);

  /**
   * Accepts an array of keys, restores specified keys to their initial value
   */
  const resetValues = useCallback(arrayOfKeys => {
    dispatch({
      type: 'RESET_VALUES',
      payload: arrayOfKeys
    });
  }, [dispatch]);

  /**
   * Empties errors generated from executing validate()
   */
  const clearErrors = useCallback(arrayOfKeys => dispatch({
    type: 'RESET_ERRORS',
    payload: arrayOfKeys
  }), [dispatch]);
  const hasChanges = useMemo(() => !isEqual(initialValues, values), [initialValues, values]);
  return {
    handleChange,
    handleSubmit,
    prepareForStaggeredSubmit,
    completeStaggeredSubmit,
    handleBlur,
    batchHandleChange,
    batchHandleBlur,
    clearValues,
    clearErrors,
    resetValues,
    hasChanges,
    values,
    errors
  };
};
export default useForm;